import type { ReactNode } from 'react';

import { cn } from '../lib/cn';

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** Right-aligned primary/secondary actions. Wraps under the title on narrow screens. */
  actions?: ReactNode;
  /** Breadcrumb or back link, rendered above the title. */
  eyebrow?: ReactNode;
  /**
   * The heading level. `h1` — the page's one heading — everywhere except where a page
   * header is rendered as a *specimen of itself*: `/design`'s gallery shows one inside
   * the real page, which gave that route two `h1`s (M13.7, WCAG 1.3.1).
   */
  as?: 'h1' | 'h2';
  className?: string;
}

/**
 * The page's h1 and its actions. Sits directly under the top bar, above the content
 * cards, so every admin screen has the same first 80px.
 */
export function PageHeader({
  title,
  description,
  actions,
  eyebrow,
  as: Heading = 'h1',
  className,
}: PageHeaderProps) {
  return (
    <header className={cn('mb-6 flex flex-wrap items-start justify-between gap-4', className)}>
      <div className="min-w-0">
        {eyebrow === undefined ? null : (
          <div className="mb-1 text-2xs font-medium tracking-badge text-neutral-400-text uppercase">
            {eyebrow}
          </div>
        )}
        <Heading className="text-3xl font-bold text-ink">{title}</Heading>
        {description === undefined ? null : (
          <p className="mt-1 max-w-2xl text-base text-neutral-500">{description}</p>
        )}
      </div>
      {actions === undefined ? null : (
        /* `max-w-full` with `shrink-0` (M11.11): `shrink-0` alone pins the row at its
           max-content width, so five toolbar buttons on a 390px phone pushed the whole
           page sideways. Capped at the header's width, `flex-wrap` does its job. */
        <div className="flex max-w-full shrink-0 flex-wrap items-center gap-3">{actions}</div>
      )}
    </header>
  );
}
